import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { UserImageWithProject } from './useUserImagesWithProjects';

interface UseInfiniteOwnedUserImagesOptions {
  searchQuery?: string;
  limit?: number;
  excludeProjectId?: string; // To exclude images already in a specific project
}

interface PageData {
  data: UserImageWithProject[];
  totalCount: number;
  hasMore: boolean;
  nextPage?: number;
}

/**
 * Hook for infinite scrolling OWNED user images with search
 * Only returns images where user_id matches the current user
 * 
 * This is different from useInfiniteUserImages which uses get_user_accessible_images
 * and returns ALL accessible images (owned + from workbooks with editor access)
 * 
 * Use this hook for:
 * - Storage import (only show images the user owns)
 * - Project import (only show images the user owns)
 * 
 * Use useInfiniteUserImages for:
 * - TipTap editor (should show all accessible images)
 */
export const useInfiniteOwnedUserImages = (options: UseInfiniteOwnedUserImagesOptions = {}) => {
  const { user } = useAuth();
  const { searchQuery = '', limit = 50, excludeProjectId } = options;

  return useInfiniteQuery<PageData, Error, { pages: PageData[]; pageParams: number[] }, (string | number)[], number>({
    queryKey: ['user-owned-images', user?.id, searchQuery, limit, excludeProjectId],
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }): Promise<PageData> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const offset = pageParam * limit;

      // Build query to get only images owned by the current user
      let query = supabase
        .from('workbook_images')
        .select('*')
        .eq('user_id', user.id) // Only images owned by the current user
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      // Add search filter if provided
      if (searchQuery?.trim()) {
        query = query.or(`generation_prompt.ilike.%${searchQuery}%,alt_text.ilike.%${searchQuery}%,filename.ilike.%${searchQuery}%`);
      }

      // Exclude images from a specific project if specified
      // We want to show: private images (workbook_id = null) + images from other projects
      if (excludeProjectId) {
        query = query.or(`workbook_id.is.null,workbook_id.neq.${excludeProjectId}`);
      }

      // Add pagination
      query = query.range(offset, offset + limit - 1);

      const { data: images, error, count } = await query;

      if (error) {
        console.error('Error fetching owned user images:', error);
        throw error;
      }

      if (!images || images.length === 0) {
        return {
          data: [],
          totalCount: 0,
          hasMore: false,
          nextPage: undefined
        };
      }

      // Get unique workbook IDs to fetch project titles
      const workbookIds = [...new Set(
        images
          .filter(img => img.workbook_id)
          .map(img => img.workbook_id)
      )];
      
      let projects: Record<string, { id: string; title: string }> = {};
      if (workbookIds.length > 0) {
        // Fetch project titles for workbook context
        const { data: workbookData, error: workbookError } = await supabase
          .from('workbook')
          .select('id, title')
          .in('id', workbookIds);
          
        if (!workbookError && workbookData) {
          projects = workbookData.reduce((acc, wb) => {
            acc[wb.id] = { id: wb.id, title: wb.title };
            return acc;
          }, {} as Record<string, { id: string; title: string }>);
        }
      }

      // Transform to match UserImageWithProject interface
      const transformedData: UserImageWithProject[] = images.map(item => ({
        id: item.id,
        image_url: item.image_url,
        filename: item.filename,
        user_id: item.user_id,
        workbook_id: item.workbook_id,
        created_at: item.created_at,
        generation_prompt: item.generation_prompt,
        width: item.width,
        height: item.height,
        // Add project information if available
        project: item.workbook_id && projects[item.workbook_id] ? projects[item.workbook_id] : null
      }));

      const hasMore = images.length === limit;

      return {
        data: transformedData,
        totalCount: count || transformedData.length,
        hasMore,
        nextPage: hasMore ? pageParam + 1 : undefined
      };
    },
    getNextPageParam: (lastPage: PageData) => lastPage.nextPage,
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};